// Roteador de dados (react-router 7). Rotas espelham os escopos da API (Parte 14.2):
// /network/*, /t/:tenantCode/*, /t/:tenantCode/s/:studyCode/*, /me/*, /confirm/:token, /no-access, /select-context, 404.
import { type RouteObject, createBrowserRouter, createMemoryRouter } from 'react-router';

import { AUTH_CALLBACK_PATH } from '../lib/auth/types';
import { MODULES } from '../modules/registry';
import type { ModuleRouteHandle } from '../modules/routeHandle';
import type { ModuleDefinition } from '../modules/types';
import { InboxItemPage } from '../modules/workflow/InboxItemPage';
import { MyDelegationsPage } from '../modules/authz/delegations/MyDelegationsPage';
import { MyAccessReviewsPage } from '../modules/authz/me/MyAccessReviewsPage';
import { MyTwoKeyPage } from '../modules/authz/me/MyTwoKeyPage';
import { MyRegistrationPage } from '../modules/people/me/MyRegistrationPage';
import { InboxPage } from '../modules/workflow/InboxPage';
import { PreferencesPage } from '../modules/notifications/PreferencesPage';
import { AccountPage } from '../pages/Account';
import { AuthCallbackPage } from '../pages/AuthCallback';
import { ConfirmPage } from '../pages/Confirm';
import { NetworkHomePage, TenantHomePage } from '../pages/Home';
import { LandingPage } from '../pages/Landing';
import { NoAccessPage } from '../pages/NoAccess';
import { NotFoundPage } from '../pages/NotFound';
import { SelectContextPage } from '../pages/SelectContext';
import { App, AuthGate, NetworkScopeLayout, RouteErrorPage, TenantScopeLayout } from './App';

function moduleRoutes(scope: 'network' | 'tenant'): RouteObject[] {
  return MODULES.flatMap((module: ModuleDefinition): RouteObject[] => {
    const entry = scope === 'network' ? module.network : module.tenant;
    if (!entry) {
      return [];
    }
    const handle: ModuleRouteHandle = {
      moduleLabelKey: entry.labelKey,
      milestone: module.milestone,
    };
    return [
      {
        path: `${entry.path}/*`,
        handle,
        lazy: async () => {
          const loaded = await module.load();
          return { Component: loaded.Component };
        },
      },
    ];
  });
}

/** Rotas de rede extras (settings/flags, saúde da plataforma e módulos separados do menu — Parte 12.5 / 2.11). */
const networkExtraRoutes: RouteObject[] = [
  {
    path: 'research-centers/*',
    handle: { moduleLabelKey: 'researchCenters', milestone: 'M1' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/people/ClinicalPeopleDirectorySubmodule');
      return { Component: loaded.ClinicalResearchCentersManagementPage };
    },
  },
  {
    path: 'procedures/*',
    handle: { moduleLabelKey: 'procedures', milestone: 'M5' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/studies/ProceduresStandaloneModule');
      return { Component: loaded.ProceduresStandaloneModule };
    },
  },
  {
    path: 'participant-reimbursements/*',
    handle: {
      moduleLabelKey: 'participantReimbursements',
      milestone: 'M5',
    } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/studies/ParticipantReimbursementsModule');
      return { Component: loaded.ParticipantReimbursementsModule };
    },
  },
  {
    path: 'equipment/:assetId',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/EquipmentDetailPage');
      return { Component: loaded.EquipmentDetailPage };
    },
  },
  {
    path: 'facilities/assets/:assetId',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/EquipmentDetailPage');
      return { Component: loaded.EquipmentDetailPage };
    },
  },
  {
    path: 'equipment/*',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/FacilitiesModule');
      return { Component: loaded.FacilitiesModule };
    },
  },
  {
    path: 'accounting/*',
    handle: { moduleLabelKey: 'accounting', milestone: 'M6' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/suppliers/AccountingStandaloneModule');
      return { Component: loaded.AccountingStandaloneModule };
    },
  },
  {
    path: 'governance/*',
    handle: { moduleLabelKey: 'governance', milestone: 'M3' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/authz/AuthzModule');
      return { Component: loaded.GovernanceStandaloneModule };
    },
  },
  {
    path: 'scheduling/*',
    handle: { moduleLabelKey: 'scheduling', milestone: 'M11' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/scheduling/SchedulingModule');
      return { Component: loaded.SchedulingModule };
    },
  },
  {
    path: 'settings/*',
    handle: { moduleLabelKey: 'settings', milestone: 'M0' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/workflow/Placeholder');
      return { Component: loaded.Component };
    },
  },
  {
    path: 'platform-health/*',
    handle: { moduleLabelKey: 'platformHealth', milestone: 'M0' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/workflow/Placeholder');
      return { Component: loaded.Component };
    },
  },
];

const tenantExtraRoutes: RouteObject[] = [
  {
    path: 'procedures/*',
    handle: { moduleLabelKey: 'procedures', milestone: 'M5' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/studies/ProceduresStandaloneModule');
      return { Component: loaded.ProceduresStandaloneModule };
    },
  },
  {
    path: 'participant-reimbursements/*',
    handle: {
      moduleLabelKey: 'participantReimbursements',
      milestone: 'M5',
    } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/studies/ParticipantReimbursementsModule');
      return { Component: loaded.ParticipantReimbursementsModule };
    },
  },
  {
    path: 'equipment/:assetId',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/EquipmentDetailPage');
      return { Component: loaded.EquipmentDetailPage };
    },
  },
  {
    path: 'facilities/assets/:assetId',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/EquipmentDetailPage');
      return { Component: loaded.EquipmentDetailPage };
    },
  },
  {
    path: 'equipment/*',
    handle: { moduleLabelKey: 'equipment', milestone: 'M7' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/facilities/FacilitiesModule');
      return { Component: loaded.FacilitiesModule };
    },
  },
  {
    path: 'governance/*',
    handle: { moduleLabelKey: 'governance', milestone: 'M3' } satisfies ModuleRouteHandle,
    lazy: async () => {
      const loaded = await import('../modules/authz/AuthzModule');
      return { Component: loaded.GovernanceStandaloneModule };
    },
  },
];

export const routes: RouteObject[] = [
  { path: '/confirm/:token', Component: ConfirmPage, ErrorBoundary: RouteErrorPage },
  { path: AUTH_CALLBACK_PATH, Component: AuthCallbackPage, ErrorBoundary: RouteErrorPage },
  {
    path: '/',
    Component: AuthGate,
    ErrorBoundary: RouteErrorPage,
    children: [
      {
        Component: App,
        children: [
          { index: true, Component: LandingPage },
          { path: 'no-access', Component: NoAccessPage },
          { path: 'select-context', Component: SelectContextPage },
          {
            path: 'me',
            children: [
              { index: true, Component: AccountPage },
              { path: 'inbox', Component: InboxPage },
              { path: 'inbox/:kind/:id', Component: InboxItemPage },
              { path: 'preferences/notifications', Component: PreferencesPage },
              // GIY-056 (Parte 5.9 "Usuário"): itens de recertificação e pendências de duas chaves, sem escopo de tenant.
              { path: 'access-reviews', Component: MyAccessReviewsPage },
              { path: 'two-key', Component: MyTwoKeyPage },
              // GIY-075 (Parte 5.7/14): "Minhas delegações" — as pendentes de aceite e as vigentes da própria pessoa, sem escopo de tenant.
              { path: 'delegations', Component: MyDelegationsPage },
              // GIY-067 (Parte 6.7 "Pessoa"): "Meu cadastro" — contatos, disponibilidade e ausências por vínculo, certificações.
              { path: 'person', Component: MyRegistrationPage },
              { path: '*', Component: NotFoundPage },
            ],
          },
          {
            path: 'network',
            Component: NetworkScopeLayout,
            children: [
              { index: true, Component: NetworkHomePage },
              ...moduleRoutes('network'),
              ...networkExtraRoutes,
              { path: '*', Component: NotFoundPage },
            ],
          },
          {
            path: 't/:tenantCode',
            Component: TenantScopeLayout,
            children: [
              { index: true, Component: TenantHomePage },
              {
                path: 's/:studyCode',
                children: [
                  { index: true, Component: TenantHomePage },
                  ...moduleRoutes('tenant'),
                  ...tenantExtraRoutes,
                  { path: '*', Component: NotFoundPage },
                ],
              },
              ...moduleRoutes('tenant'),
              ...tenantExtraRoutes,
              { path: '*', Component: NotFoundPage },
            ],
          },
          { path: '*', Component: NotFoundPage },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}

/** Roteador em memória para testes de componentes. */
export function createTestRouter(initialEntries: string[] = ['/']) {
  return createMemoryRouter(routes, { initialEntries });
}
